import { USER_UPDATED } from '@gig/magic-messages';
import { magic } from '@gig/magic-sdk';
import { ServiceType } from '@gig/magic-sdk/dist/adapter/externalServices/requestToken';
import type { IRequestTokenParams } from '@gig/magic-sdk/dist/adapter/externalServices/requestToken';
import type { IUser } from '@gig/magic-sdk/dist/adapter/users/user';
import {
    convertToIsoDate,
    getFeatureEnabled,
    getItem,
    isValidDate,
    setItem
} from '@gig/magic-common';
import { createScriptTag, createStylesheetTag } from '../../util/script';

interface ITokensMap {
    [userId: string]: {
        /**
         * Token returned for user from Symplify API
         */
        token: string;

        /**
         * Expiration date of the token, 90 days after its creation
         * Stored in localStorage in ISO format (YYYY-MM-DD)
         */
        expirationDate: string;
    };
}

export const SYMPLIFY_FEATURE_SLUG = 'symplify-push-notifications';
const TOKENS_STORAGE_KEY = 'simplify-web-notifications-tokens';
const TOKEN_DURATION_IN_DAYS = 90;
const SCRIPT_ID = 'sym-web-push';
const STYLESHEET_ID = 'sym-web-push__stylesheet';

const SCRIPT_URL =
    'https://d3mi6d1ao3fzsg.cloudfront.net/webpush/1/webpush.min.js';
const STYLESHEET_URL =
    'https://d3mi6d1ao3fzsg.cloudfront.net/webpush/1/webpush.min.css';

export class SymplifyService {
    constructor() {
    const init = async () => {
        const symplifyFeatureEnabled = await getFeatureEnabled(SYMPLIFY_FEATURE_SLUG);
        if (symplifyFeatureEnabled) {
            this.subscribeToLoginChanges();   
        }
    }
    init();
    }

    private subscribeToLoginChanges() {
        window.eventBus.subscribe<IUser>(
            USER_UPDATED,
            async (event) => {
                const user = event.payload;
                if (!user) {
                    this.cleanupSymplifyTags();
                    return;
                }

                if (!this.hasValidToken(user)) {
                    const token = await this.requestToken(user);
                    this.persistUserToken(token, user);
                }

                this.setupSymplifyTags(user);
            },
            { replay: true },
        );
    }

    private hasValidToken(user: IUser): boolean {
        const userData = this.getUserData(user);
        if (!userData) {
            return false;
        }

        const expirationDate = new Date(userData.expirationDate);
        return (
            isValidDate(expirationDate) && !this.dateHasExpired(expirationDate)
        );
    }

    private persistUserToken(token: string, user: IUser) {
        const tokensMap: ITokensMap = getItem(TOKENS_STORAGE_KEY) ?? {};

        tokensMap[user.userId] = {
            token,
            expirationDate: convertToIsoDate(this.getExpirationDate()),
        };

        setItem(TOKENS_STORAGE_KEY, tokensMap);
    }

    private requestToken(user: IUser): Promise<string> {
        const params: IRequestTokenParams = {
            service: ServiceType.SIMPLIFY,
            userID: user.userId,
        };
        return magic.methods.externalServices.requestToken(params);
    }

    private setupSymplifyTags(user: IUser) {
        this.appendSymplifyScript(user);
        this.appendSymplifyStylesheet();
    }

    private cleanupSymplifyTags() {
        const script = document.getElementById(SCRIPT_ID);
        script?.remove();

        const stylesheet = document.getElementById(STYLESHEET_ID);
        stylesheet?.remove();

        document.querySelector('.sym-modal-window')?.remove();
        document.querySelector('.sym-popup.sym-preview')?.remove();
    }

    private appendSymplifyScript(user: IUser) {
        createScriptTag(document.body, SCRIPT_URL, {
            'id': SCRIPT_ID,
            'data-user-hash': this.getUserData(user)?.token,
            'data-notifications': 'true',
            'data-service-worker': 'scripts/service-worker.min.js',
            'async': true
        });
    }

    private appendSymplifyStylesheet() {
        createStylesheetTag(document.head, STYLESHEET_URL, {
            id: STYLESHEET_ID,
        });
    }

    private getUserData(user: IUser): ITokensMap['string'] | undefined {
        const tokensMap: ITokensMap | undefined = getItem(TOKENS_STORAGE_KEY);
        return tokensMap?.[user.userId];
    }

    private getExpirationDate(): Date {
        const expirationDate = this.today();
        expirationDate.setDate(
            expirationDate.getDate() + TOKEN_DURATION_IN_DAYS,
        );
        return expirationDate;
    }

    private dateHasExpired(date: Date): boolean {
        return date <= this.today();
    }

    private today() {
        // NOTE: it's easier to mock this in the tests instead of `new Date()`
        return new Date(Date.now());
    }
}
